import { Component } from '@theme/component';

const STORAGE_KEY = 'starbike:selectedMoto';
const CHANGE_EVENT = 'starbike:moto-change';

function readSaved() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!parsed) return null;
    if (typeof parsed.gid !== 'string' || !parsed.gid.startsWith('gid://')) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @typedef {object} Refs
 * @property {HTMLSelectElement} yearSelect
 * @property {HTMLSelectElement} modelSelect
 * @property {HTMLElement} resultCard
 * @property {HTMLImageElement} resultImage
 * @property {HTMLElement} resultYear
 * @property {HTMLElement} resultModel
 * @property {HTMLButtonElement} [clear]
 *
 * @extends {Component<Refs>}
 */
class MotorcycleSelector extends Component {
  requiredRefs = ['yearSelect', 'modelSelect'];

  connectedCallback() {
    super.connectedCallback();
    this.byYear = new Map();
    this.placeholderImage = this.dataset.placeholderImage || '';
    this.#index(window.STARBIKE_MOTORCYCLES || []);
    this.#populateYears();
    this.#applyState(readSaved(), { silent: true });

    this.refs.yearSelect.addEventListener('change', this.#onYearChange);
    this.refs.modelSelect.addEventListener('change', this.#onModelChange);
    if (this.refs.clear) this.refs.clear.addEventListener('click', this.#onClear);

    document.addEventListener(CHANGE_EVENT, this.#onExternalChange);
    window.addEventListener('storage', this.#onStorage);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(CHANGE_EVENT, this.#onExternalChange);
    window.removeEventListener('storage', this.#onStorage);
  }

  /**
   * @param {Array<{gid:string,year:string,label:string,displayName:string,handle:string,image:string|null}>} list
   */
  #index(list) {
    for (const m of list) {
      const y = String(m.year);
      if (!this.byYear.has(y)) this.byYear.set(y, []);
      this.byYear.get(y).push(m);
    }
  }

  #populateYears() {
    const years = Array.from(this.byYear.keys()).sort((a, b) => Number(b) - Number(a));
    const sel = this.refs.yearSelect;
    for (const y of years) {
      const opt = document.createElement('option');
      opt.value = y;
      opt.textContent = y;
      sel.appendChild(opt);
    }
  }

  /**
   * @param {string} year
   */
  #resetModelSelect() {
    const sel = this.refs.modelSelect;
    sel.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = sel.dataset.placeholder || 'Modèle';
    sel.appendChild(placeholder);
    sel.disabled = true;
  }

  #populateModels(year) {
    this.#resetModelSelect();
    const sel = this.refs.modelSelect;
    const list = this.byYear.get(String(year)) || [];
    for (const m of list) {
      const opt = document.createElement('option');
      opt.value = m.gid;
      opt.textContent = m.label;
      sel.appendChild(opt);
    }
    sel.disabled = list.length === 0;
  }

  #onYearChange = () => {
    const year = this.refs.yearSelect.value;
    if (!year) {
      this.#resetModelSelect();
      return;
    }
    this.#populateModels(year);
  };

  #onModelChange = () => {
    const gid = this.refs.modelSelect.value;
    if (!gid) return;
    const year = this.refs.yearSelect.value;
    const option = this.refs.modelSelect.selectedOptions[0];
    const moto = this.#findMoto(gid);
    const payload = { gid, year, label: option ? option.textContent : '', image: moto?.image || null };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    this.#applyState(payload, { silent: false });
  };

  /**
   * @param {string} gid
   */
  #findMoto(gid) {
    const target = String(gid);
    for (const list of this.byYear.values()) {
      for (const m of list) {
        if (String(m.gid) === target) return m;
      }
    }
    return null;
  }

  #onClear = (event) => {
    event.preventDefault();
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    this.#applyState(null, { silent: false });
  };

  #onExternalChange = (event) => {
    if (event.target === this) return;
    this.#applyState(event.detail || null, { silent: true });
  };

  #onStorage = (event) => {
    if (event.key !== STORAGE_KEY) return;
    this.#applyState(readSaved(), { silent: true });
  };

  /**
   * Sync this instance's UI to `saved`, optionally broadcasting a change event.
   * @param {{gid:string,year:string,label:string,image:string|null}|null} saved
   * @param {{silent:boolean}} opts
   */
  #applyState(saved, { silent }) {
    if (saved && saved.gid) {
      this.refs.yearSelect.value = String(saved.year);
      this.#populateModels(saved.year);
      this.refs.modelSelect.value = saved.gid;
      this.dataset.hasSelection = 'true';

      // Update result card
      if (this.refs.resultImage) {
        this.refs.resultImage.src = saved.image || this.placeholderImage;
        this.refs.resultImage.alt = saved.label || '';
      }
      if (this.refs.resultYear) {
        this.refs.resultYear.textContent = saved.year;
      }
      if (this.refs.resultModel) {
        this.refs.resultModel.textContent = saved.label || '';
      }
    } else {
      this.refs.yearSelect.value = '';
      this.#resetModelSelect();
      delete this.dataset.hasSelection;

      if (this.refs.resultImage) {
        this.refs.resultImage.src = this.placeholderImage;
        this.refs.resultImage.alt = '';
      }
      if (this.refs.resultYear) this.refs.resultYear.textContent = '';
      if (this.refs.resultModel) this.refs.resultModel.textContent = '';
    }
    if (!silent) {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
        detail: saved,
        bubbles: true,
      }));
    }
  }
}

if (!customElements.get('motorcycle-selector')) {
  customElements.define('motorcycle-selector', MotorcycleSelector);
}

/* ---------------------------------------------------------------------------
 * Collection/search filter bridge
 *
 * Translates the user's motorcycle selection into a programmatic tick of the
 * matching `filter.p.m.custom.compatible_motorcycles` facet checkbox, so the
 * theme's existing facet AJAX flow (`facet-inputs-component` + `updateFilters`)
 * refreshes the product grid in place. Does nothing on pages without the
 * facet rendered (home, product, etc.).
 * ------------------------------------------------------------------------- */

const FILTER_NAME = 'filter.p.m.custom.compatible_motorcycles';

function findFacetCheckboxes() {
  return document.querySelectorAll(`input[type="checkbox"][name="${FILTER_NAME}"]`);
}

function notifyOnePerForm(changedBoxes) {
  const seenForms = new Set();
  for (const box of changedBoxes) {
    const form = box.closest('facet-inputs-component') || box.closest('form');
    if (form && seenForms.has(form)) continue;
    if (form) seenForms.add(form);
    box.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function applyFilter(gid) {
  const boxes = findFacetCheckboxes();
  if (!boxes.length) return;

  const universal = window.STARBIKE_UNIVERSAL_GID;
  const changedBoxes = [];
  for (const box of boxes) {
    const shouldCheck = box.value === gid || (universal && box.value === universal);
    if (box.checked !== shouldCheck) {
      box.checked = shouldCheck;
      changedBoxes.push(box);
    }
  }
  notifyOnePerForm(changedBoxes);
}

function clearFilter() {
  const boxes = findFacetCheckboxes();
  if (!boxes.length) return;

  const changedBoxes = [];
  for (const box of boxes) {
    if (box.checked) {
      box.checked = false;
      changedBoxes.push(box);
    }
  }
  notifyOnePerForm(changedBoxes);
}

function syncFilterToStorage() {
  const saved = readSaved();
  if (saved && saved.gid) {
    const url = new URL(window.location.href);
    if (url.searchParams.has(FILTER_NAME)) return;
    applyFilter(saved.gid);
  } else {
    clearFilter();
  }
}

function initFilterBridge() {
  syncFilterToStorage();

  document.addEventListener(CHANGE_EVENT, (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail && detail.gid) applyFilter(detail.gid);
    else clearFilter();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    syncFilterToStorage();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFilterBridge, { once: true });
} else {
  initFilterBridge();
}
